import { Component, Input, OnInit } from '@angular/core';
import { map } from 'rxjs';

import { SettingsService } from '../service/settings.service';
import { ArmyBuilderConfig, SettingsFieldConfig, SettingsGameConfig } from '../../config';

@Component({
    standalone: false,
    selector: 'abs-settings',
    template: `
        <ng-container *ngLet="{ settings: settings$ | async } as props">
            <abs-list [items]="fields" [template]="settingsTemplate" [showDelete]="false"></abs-list>
            <ng-template #settingsTemplate let-key="item[0]" let-field="item[1]">
                <div class="itemWrap">
                    <div class="itemName">
                        <h2>{{ 'GLOBAL.SETTINGS.' + (gameId | uppercase) + '.' + (key | uppercase) + '.TITLE' | translate }}</h2>
                    </div>
                    <div class="itemDescription">
                        <p>{{ 'GLOBAL.SETTINGS.' + (gameId | uppercase) + '.' + (key | uppercase) + '.DESCRIPTION' | translate }}</p>
                    </div>
                </div>
                <div class="itemValue {{ key }}">
                    @if (field.type === 'select') {
                        <ion-select
                            *ngIf="field.type === 'select'"
                            [value]="props.settings?.[key] ?? field.defaultValue"
                            (ionChange)="settingsService.updateValue(gameId, key, $event.detail.value)"
                            slot="end"
                        >
                            <ion-select-option *ngFor="let option of field.options" [value]="option.value">
                                {{ option.label }}
                            </ion-select-option>
                        </ion-select>
                    } @else if (field.type === 'number') {
                        <ion-input
                            type="number"
                            inputmode="numeric"
                            slot="end"
                            [value]="props.settings?.[key] ?? field.defaultValue"
                            [min]="field.min ?? 1"
                            [placeholder]="field.placeholder ?? ''"
                            (ionChange)="numberSettingChanged(key, $event)"
                        ></ion-input>
                    } @else {
                        <ion-toggle
                            [slot]="'end'"
                            *ngIf="field.type === 'boolean'"
                            [checked]="!!(props.settings?.[key] ?? field.defaultValue)"
                            (ionChange)="toggleChanged(key, $event)"
                        ></ion-toggle>
                    }
                </div>
            </ng-template>
        </ng-container>
    `,
    styles: [
        `
            h2 {
                margin-top: 0;
            }

            /*
             * Both controls sit in .itemValue, which is display: flex, next to
             * the setting's title and description. min-width: 0 because a flex
             * item defaults to min-width: auto and so refuses to shrink below
             * its intrinsic width, pushing the row past the viewport on narrow
             * screens.
             */
            ion-select,
            ion-input {
                min-width: 0;
            }

            /*
             * The number inputs need an explicit width. Ionic's inner
             * .native-input is flex: 1 with width: 100%, so against an auto-width
             * host that percentage has nothing definite to resolve against and
             * the browser falls back to the input's intrinsic ~20-character
             * size — which is why leaving the host auto doesn't shrink it.
             *
             * Set on the host rather than on .native-input so this doesn't
             * depend on Ionic's internal class names, and in rem rather than px
             * so it scales with the user's font size instead of clipping when
             * text is enlarged. 4.5rem is 72px at the default 16px root.
             *
             * Ionic sizes inputs slotted into an ion-item via
             * "ion-item [slot=end].sc-ion-input-md-h", which outranks a bare
             * ion-input rule. Naming the wrapper and the slot attribute wins on
             * specificity instead, which keeps the value overridable later —
             * !important here would have to be answered with more !important.
             */
            .itemValue ion-input[slot='end'] {
                width: 4.5rem;
            }
        `
    ]
})
export class SettingsComponent implements OnInit {
    @Input({ required: true })
    gameId: string;

    settings$ = this.settingsService.settings$.pipe(map((settings) => settings[this.gameId] ?? {}));
    fields: [string, SettingsFieldConfig][];
    constructor(
        public settingsService: SettingsService,
        public config: ArmyBuilderConfig
    ) {
        console.log(this.config.settings);
    }

    ngOnInit() {
        this.fields = Object.entries(this.config.settings[this.gameId] || {});
    }

    toggleChanged(key: string, event: any) {
        this.settingsService.updateValue(this.gameId, key, event.detail.checked);
    }

    numberSettingChanged(key: string, event: any) {
        const raw = event.detail.value;
        const parsed = parseInt(raw, 10);
        this.settingsService.updateValue(this.gameId, key, Number.isFinite(parsed) && parsed > 0 ? `${parsed}` : '');
    }
}
